import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import { LuCalendar } from 'react-icons/lu'
import Button from 'new-components/Button'
import { Input, DatePicker, DatePickerProps, ConfigProvider } from 'antd'
import FilterDropdown from 'new-components/MarketPlace/FilterDropdown'
import { filterMobile } from 'assets/svg'
import { useConsultation } from 'pages/MyQueries/hooks/useConsultation'
import CardQueries from '../CardQueries'
import { SessionCallCard } from '../SessionCallCard'
import { useQueries } from 'pages/MyQueries/hooks/useQueries'
import Pagination from 'components/Pagination'
import { useDebouncedSearch } from 'pages/MyQueries/hooks/useDebounceSearch'
import locale from 'antd/es/locale/pt_BR'
import { AiOutlineCloseCircle } from 'react-icons/ai'
import { CONSULTATION_FILTER } from 'pages/MyQueries/Mocks/ListAppointments'
import InstantConsultationBanner from 'new-components/InstantConsultationBanner'
import {
  getSessionCallsByUser,
  normalizeSessionCallListItem
} from '@cannect/services/resources/instantConsultation/requests'
import type { TSessionCallListItem } from '@cannect/services/resources/instantConsultation/types'
import * as Styles from './styles'

export interface AppointmentFormProps {
  filter: string
  scheduling_date: string | null
  scheduling_time: string | null
  coupon: string
  amountWithDiscount: string
  address: {
    cep: string
    city: string
    address: string
    number: string
    state: string
  }
  payment: {
    card_holder_name: string
    card_month_validate: string
    card_year_validate: string
    card_number: string
    card_cvv: string
    payment_method: string
  }
}

function ConsultationGroups({ groupedConsultations }: { groupedConsultations: any }) {
  return (
    <Styles.ListAppointments>
      {Object.entries(groupedConsultations)
        .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA))
        .map(([year, consultations]: any) => (
          <Styles.GroupedConsultationsList key={year}>
            <h2>{year}</h2>

            {(consultations || []).map((item: any) => (
              <CardQueries key={item.id} {...item} />
            ))}
          </Styles.GroupedConsultationsList>
        ))}
    </Styles.ListAppointments>
  )
}

function ListAppointments() {
  const { pathname } = useLocation()
  const history = useHistory()
  const isAspaecArea = pathname.toLowerCase().startsWith('/areaassociados')
  const { groupConsultationsByYear } = useConsultation()
  const { consultations, getConsultationList, loadingConsultation, reloadList, count } = useQueries()
  const [loading, setLoading] = useState(false)
  const [sessionCalls, setSessionCalls] = useState<TSessionCallListItem[]>([])
  const groupedConsultations = groupConsultationsByYear(consultations)
  const [selectedDate, setSelectedDate] = useState<any>(null)
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [sortFilter, setSortFilter] = useState({ title: '', value: '' })
  const limit = 6

  const handleSearch = async ({ term, sortFilter: sort }: { term?: any; sortFilter?: string }) => {
    setLoading(true)
    try {
      await getConsultationList({ limit, filterDate: sort, search: term, page })
    } catch {
      // erro já tratado em useQueries
    }
    if (isAspaecArea) {
      setSessionCalls([])
      setLoading(false)
      return
    }
    try {
      const raw = await getSessionCallsByUser({ limit: 50, page: 1 })
      setSessionCalls(raw.map((item) => normalizeSessionCallListItem(item)))
    } catch {
      setSessionCalls([])
    } finally {
      setLoading(false)
    }
  }

  const { searchTerm, handleSearchChange } = useDebouncedSearch((term: string) => {
    handleSearch({ term, sortFilter: sortFilter.value || undefined })
    setSelectedDate(null)
  }, 1000)

  useEffect(() => {
    handleSearch({ term: searchTerm, sortFilter: sortFilter.value || undefined })
  }, [reloadList, page])

  const handleDateChange: DatePickerProps['onChange'] = (date, dateString) => {
    if (date) {
      handleSearch({ term: dateString, sortFilter: sortFilter.value || undefined })
      setSelectedDate(date)
      setSortFilter({ title: '', value: '' })
      setSearchInput('')
    }
  }

  const handleClearDate = () => {
    handleSearch({ term: '', sortFilter: sortFilter.value || undefined })
    setSelectedDate(null)
  }

  const handleSortFilter = ({ title, value }: { title: string; value: string }) => {
    handleSearch({ term: searchTerm, sortFilter: value })
    setSelectedDate(null)
    setSortFilter({ title, value })
    setSearchInput('')
  }

  const handleChangeInput = (value: string) => {
    setSearchInput(value)
    handleSearchChange(value)
    setSelectedDate(null)
    setSortFilter({ title: '', value: '' })
  }

  const listLoading = loadingConsultation || loading

  return (
    <Styles.Wrapper>
      {!isAspaecArea && <InstantConsultationBanner />}

      <Styles.WhiteCard>
        <Styles.HeadingRow>
          <Styles.Heading>
            <LuCalendar className="calendar-icon" />
            <span>Minhas consultas</span>
          </Styles.Heading>
          <Button
            background="green"
            onClick={() => history.push(isAspaecArea ? '/aspaec/agendamento' : '/agendamento')}>
            Nova consulta
          </Button>
        </Styles.HeadingRow>

        {!listLoading && sessionCalls.length > 0 && (
          <>
            <Styles.SectionSubtitle>Consulta na hora</Styles.SectionSubtitle>
            {sessionCalls.map((s) => (
              <SessionCallCard key={`session-call-${s.id}`} session={s} />
            ))}
          </>
        )}

        <Styles.FilterBox>
          <Styles.SearchBox>
            <Input
              placeholder="Pesquisar Consultas"
              disabled={loading}
              value={searchInput}
              onChange={(e) => handleChangeInput(e.target.value)}
            />
          </Styles.SearchBox>

          <Styles.Filters>
            <FilterDropdown
              title="Todas"
              imageIcon={filterMobile}
              data={CONSULTATION_FILTER}
              selectedValue={sortFilter}
              onChangeValue={handleSortFilter}
            />
            <Styles.Separator />
            <ConfigProvider locale={locale}>
              <DatePicker
                placeholder="Pesquisa por datas"
                value={selectedDate}
                onChange={handleDateChange}
                format="YYYY-MM-DD"
                bordered={false}
                allowClear={{ clearIcon: <AiOutlineCloseCircle onClick={handleClearDate} /> }}
              />
            </ConfigProvider>
            <Styles.Separator />
            <Styles.ConsultationCount>
              {consultations.length} agendamentos
              {sessionCalls.length > 0
                ? ` · ${sessionCalls.length} consulta${sessionCalls.length !== 1 ? 's' : ''} na hora`
                : ''}
            </Styles.ConsultationCount>
          </Styles.Filters>
        </Styles.FilterBox>

        {listLoading ? (
          <Styles.LoadingContent>Loading</Styles.LoadingContent>
        ) : (
          <ConsultationGroups groupedConsultations={groupedConsultations} />
        )}

        <Styles.PaginateBox>
          {count > 1 && (
            <Pagination
              forcePage={page - 1}
              onPageChange={({ selected }: any) => setPage(selected + 1)}
              pageCount={count}
            />
          )}
        </Styles.PaginateBox>
      </Styles.WhiteCard>
    </Styles.Wrapper>
  )
}

export default ListAppointments
